import React, { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { CartProvider } from "./context/CartContext";
import { initAnalytics, trackPageView } from "./lib/analytics";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import NewsletterModal from "./components/NewsletterModal";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductPage from "./pages/ProductPage";
import PolicyPage from "./pages/PolicyPage";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-ink text-bone flex flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="font-display font-extrabold uppercase tracking-tighter text-3xl">Something broke.</p>
          <p className="text-ash text-sm">{String(this.state.error.message || this.state.error)}</p>
          <button onClick={() => window.location.reload()} className="bg-bone text-ink font-mono text-xs tracking-[0.2em] px-8 py-3.5">
            RELOAD
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function SmoothScroll() {
  useEffect(() => {
    let lenis;
    let rafId;
    let cancelled = false;
    import("lenis")
      .then(({ default: Lenis }) => {
        if (cancelled) return;
        lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
        const raf = (t) => {
          lenis.raf(t);
          rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (lenis) lenis.destroy();
    };
  }, []);
  return null;
}

function RouteEffects() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  return null;
}

export default function App() {
  useEffect(() => {
    initAnalytics();
  }, []);
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <CartProvider>
          <SmoothScroll />
          <RouteEffects />
          <div className="grain min-h-screen bg-ink text-bone selection:bg-bone selection:text-ink">
            <Nav />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/product/:handle" element={<ProductPage />} />
                <Route path="/policies/:slug" element={<PolicyPage />} />
                <Route path="*" element={<Home />} />
              </Routes>
            </main>
            <Footer />
            <CartDrawer />
            <NewsletterModal />
          </div>
          <Toaster
            theme="dark"
            position="bottom-center"
            toastOptions={{
              style: { background: "#17171A", color: "#F2F2EF", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 0 },
            }}
          />
        </CartProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
